import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  formatMoney,
  ORDER_STATUS_LABELS,
  ORDER_STATUSES,
} from "@mavatech/shared";
import { Button, Dialog, Input, Select, Textarea } from "@/components/ui";
import { OrderJournalSection } from "./OrderJournalSection";
import { CustomerCombobox } from "@/components/customers/CustomerCombobox";
import { trpc } from "@/lib/trpc";
import { useAppStore } from "@/stores/app.store";

const QUOTE_STATUS_LABEL: Record<string, string> = {
  draft: "Entwurf",
  sent: "versendet",
  accepted: "angenommen",
  rejected: "abgelehnt",
  expired: "abgelaufen",
};
const INVOICE_STATUS_LABEL: Record<string, string> = {
  draft: "Entwurf",
  sent: "ausgestellt",
  partially_paid: "teilbezahlt",
  paid: "bezahlt",
  overdue: "überfällig",
  cancelled: "storniert",
};

const INVOICE_KIND_LABEL: Record<string, string> = {
  standalone: "Rechnung",
  advance: "Anzahlung",
  partial: "Teilrechnung",
  final: "Schlussrechnung",
  credit_note: "Gutschrift",
};

interface State {
  open: boolean;
  orderId?: string | null;
  /** vorbelegter Kunde, wenn der Dialog von einer Kundenseite aus geöffnet wird */
  customerId?: string | null;
}

export function OrderDialog({
  state,
  onClose,
}: {
  state: State;
  onClose: () => void;
}) {
  const utils = trpc.useUtils();
  const addToast = useAppStore((s) => s.addToast);
  const editing = !!state.orderId;
  const detail = trpc.order.get.useQuery(
    { id: state.orderId! },
    { enabled: state.open && editing },
  );
  const members = trpc.auth.members.useQuery();

  const [customerId, setCustomerId] = useState("");
  const [propertyId, setPropertyId] = useState("");
  const [quoteId, setQuoteId] = useState("");
  const [title, setTitle] = useState("");
  const [scheduledDate, setScheduledDate] = useState("");
  const [assigneeUserId, setAssigneeUserId] = useState("");
  const [status, setStatus] = useState("ordered");
  const [description, setDescription] = useState("");
  const [materialNotes, setMaterialNotes] = useState("");

  const properties = trpc.property.list.useQuery(
    { customerId },
    { enabled: !!customerId },
  );
  const quotes = trpc.quote.list.useQuery(
    { customerId },
    { enabled: !!customerId },
  );

  useEffect(() => {
    if (!state.open) return;
    if (editing && detail.data) {
      const d = detail.data;
      setCustomerId(d.customer.id);
      setPropertyId(d.propertyId ?? "");
      setQuoteId(d.quoteId ?? "");
      setTitle(d.title);
      setScheduledDate(
        d.scheduledDate
          ? new Date(d.scheduledDate).toISOString().slice(0, 10)
          : "",
      );
      setAssigneeUserId(d.assigneeUserId ?? "");
      setStatus(d.status);
      setDescription(d.description ?? "");
      setMaterialNotes(d.materialNotes ?? "");
    } else if (!editing) {
      setCustomerId(state.customerId ?? "");
      setPropertyId("");
      setQuoteId("");
      setTitle("");
      setScheduledDate("");
      setAssigneeUserId("");
      setStatus("ordered");
      setDescription("");
      setMaterialNotes("");
    }
  }, [state.open, editing, detail.data, state.customerId]);

  const refresh = () => {
    utils.order.list.invalidate();
    if (state.orderId) utils.order.get.invalidate({ id: state.orderId });
  };
  const onErr = (e: { message: string }) =>
    addToast({ type: "error", title: "Fehler", description: e.message });
  const createM = trpc.order.create.useMutation({
    onSuccess: () => {
      refresh();
      addToast({ type: "success", title: "Auftrag angelegt" });
      onClose();
    },
    onError: onErr,
  });
  const updateM = trpc.order.update.useMutation({
    onSuccess: () => {
      refresh();
      addToast({ type: "success", title: "Gespeichert" });
    },
    onError: onErr,
  });
  const setStatusM = trpc.order.setStatus.useMutation({ onSuccess: refresh });

  const payload = () => ({
    customerId,
    propertyId: propertyId || null,
    quoteId: quoteId || null,
    title: title.trim(),
    scheduledDate: scheduledDate ? new Date(scheduledDate) : null,
    assigneeUserId: assigneeUserId || null,
    description: description.trim() || null,
    materialNotes: materialNotes.trim() || null,
  });

  const submit = () => {
    if (!customerId || !title.trim()) return;
    if (editing) updateM.mutate({ id: state.orderId!, ...payload() });
    else createM.mutate(payload());
  };

  const linkedInvoices = editing ? (detail.data?.invoices ?? []) : [];
  const hasDoc = !!quoteId || linkedInvoices.length > 0;
  const materialItems = editing ? (detail.data?.materialItems ?? []) : [];

  return (
    <Dialog
      open={state.open}
      onOpenChange={(v) => !v && onClose()}
      title={
        editing
          ? `Auftrag ${detail.data?.number ?? ""} bearbeiten`
          : "Neuer Auftrag"
      }
      full
    >
      <div className="grid grid-cols-3 gap-4">
        <CustomerCombobox
          value={customerId}
          onChange={(id) => {
            setCustomerId(id);
            setPropertyId("");
            setQuoteId("");
          }}
        />
        <Select
          label="Objekt (optional)"
          value={propertyId}
          onChange={(e) => setPropertyId(e.target.value)}
          disabled={!customerId}
          options={[
            {
              value: "",
              label: customerId ? "— kein Objekt —" : "erst Kunde wählen",
            },
            ...(properties.data ?? []).map((p) => ({
              value: p.id,
              label: p.label,
            })),
          ]}
        />
        <Input
          label="Betreff"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        <Select
          label="Angebot (optional)"
          value={quoteId}
          onChange={(e) => setQuoteId(e.target.value)}
          disabled={!customerId}
          options={[
            { value: "", label: "— kein Angebot —" },
            ...(quotes.data ?? []).map((q) => ({
              value: q.id,
              label: `${q.number ?? "Entwurf"} · ${q.title}`,
            })),
          ]}
        />
        <Input
          label="Termin (optional)"
          type="date"
          value={scheduledDate}
          onChange={(e) => setScheduledDate(e.target.value)}
        />
        <Select
          label="Techniker (optional)"
          value={assigneeUserId}
          onChange={(e) => setAssigneeUserId(e.target.value)}
          options={[
            { value: "", label: "— nicht zugewiesen —" },
            ...(members.data ?? []).map((m) => ({
              value: m.userId,
              label: m.name,
            })),
          ]}
        />
        {editing && (
          <Select
            label="Status"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setStatusM.mutate({
                id: state.orderId!,
                status: e.target.value as never,
              });
            }}
            options={ORDER_STATUSES.map((s) => ({
              value: s,
              label: ORDER_STATUS_LABELS[s],
            }))}
          />
        )}
      </div>

      {editing && (detail.data?.quote || linkedInvoices.length > 0) && (
        <div className="mt-4">
          <p className="label mb-1.5">Verknüpfte Dokumente</p>
          <div className="space-y-1">
            {detail.data?.quote && (
              <Link
                to={`/angebote/${detail.data.quote.id}`}
                className="flex items-center justify-between card p-2 text-sm hover:bg-bg-hover"
              >
                <span className="text-text-primary">
                  Angebot {detail.data.quote.number ?? "Entwurf"} ·{" "}
                  {detail.data.quote.title}
                  <span className="text-text-tertiary text-xs ml-2">
                    {QUOTE_STATUS_LABEL[detail.data.quote.status] ??
                      detail.data.quote.status}
                    {" · "}
                    {new Date(
                      detail.data.quote.sentAt ?? detail.data.quote.issueDate,
                    ).toLocaleDateString("de-AT")}
                  </span>
                </span>
                <span className="text-xs text-accent">öffnen</span>
              </Link>
            )}
            {linkedInvoices.map((i) => (
              <Link
                key={i.id}
                to={`/rechnungen/${i.id}`}
                className="flex items-center justify-between card p-2 text-sm hover:bg-bg-hover"
              >
                <span className="text-text-primary">
                  {INVOICE_KIND_LABEL[i.kind] ?? i.kind} {i.number ?? "Entwurf"}{" "}
                  · {i.title}
                  <span className="text-text-tertiary text-xs ml-2">
                    {INVOICE_STATUS_LABEL[i.status] ?? i.status}
                    {" · "}
                    {new Date(i.issueDate).toLocaleDateString("de-AT")}
                  </span>
                </span>
                <span className="text-money text-text-secondary">
                  {formatMoney(i.grossCents)}
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="mt-4">
        <Textarea
          label="Beschreibung (optional)"
          rows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>

      <div className="mt-3">
        {hasDoc ? (
          <div>
            <p className="label mb-1.5">
              Verwendetes Material (aus verknüpftem Dokument)
            </p>
            {!materialItems.length ? (
              <p className="text-sm text-text-tertiary">
                {editing
                  ? "Keine Material-Positionen im verknüpften Dokument."
                  : "Wird nach dem Speichern angezeigt."}
              </p>
            ) : (
              <ul className="text-sm text-text-secondary space-y-0.5">
                {materialItems.map((m, i) => (
                  <li key={i}>
                    {m.quantity} {m.unit} · {m.description}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : (
          <Textarea
            label="Verwendetes Material (Freitext)"
            rows={3}
            value={materialNotes}
            onChange={(e) => setMaterialNotes(e.target.value)}
            hint="Nur relevant ohne verknüpftes Angebot/Rechnung — sonst kommt die Materialliste von dort."
          />
        )}
      </div>

      {editing && (
        <div className="mt-5 border-t border-border-subtle pt-4">
          <OrderJournalSection orderId={state.orderId!} />
        </div>
      )}

      <div className="flex justify-end gap-2 mt-5">
        <Button variant="ghost" onClick={onClose}>
          Schließen
        </Button>
        <Button
          variant="primary"
          onClick={submit}
          loading={createM.isPending || updateM.isPending}
          disabled={!customerId || !title.trim()}
        >
          Speichern
        </Button>
      </div>
    </Dialog>
  );
}
